import React, { useCallback, useState } from "react";
import Button from "@ui/Button";
import Text from "@ui/Text";
import { PlayCircleLine } from "@ui/IconV2/react";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { usePost } from "../../../api/useCall";
import Modal from "@ui/Modal";
import useModal from "@ui/lib/hooks/useModal";

interface Props {
  canEdit: boolean;
}

interface MigrationFailure {
  projectId: string;
  error: string;
}

export function ManualFaultMigration({ canEdit }: Props) {
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const { modal: modalIsOpen, openModal, closeModal } = useModal();
  const [failureDetails, setFailureDetails] = useState<MigrationFailure[]>([]);

  const executeMigration = usePost("/manualFaultEvent/migration/execute");

  const handleMigration = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await executeMigration({});
      const {
        migratedProjectsCount,
        migratableProjectsCount,
        failedProjectMigrationsCount,
        migrationFailures,
        blockedProjectsCount: blockedCount,
      } = response.body;

      const message = t("customization.section.failure_analysis.migration.execute.success", {
        migratedProjectsCount,
        migratableProjectsCount,
        failedProjectMigrationsCount,
        blockedCount,
      });

      if (failedProjectMigrationsCount > 0 && migrationFailures) {
        setFailureDetails(migrationFailures);
        addToast(
          "warning",
          <div>
            <div>{message}</div>
            <Button
              variant="link"
              onClick={() => {
                openModal();
              }}
              disabled={false}
            >
              {t("customization.section.failure_analysis.migration.execute.show_more")}
            </Button>
          </div>,
        );
      } else {
        addToast("success", message);
      }
    } catch (error) {
      console.debug({ error });
      addToast("error", t("customization.section.failure_analysis.migration.execute.error"));
    } finally {
      setIsLoading(false);
    }
  }, [executeMigration, addToast, openModal]);

  if (!canEdit) {
    return null;
  }

  return (
    <>
      <div className={migrationContainer}>
        <Text type="body1" as="p" className={textContainer}>
          <span>{t("customization.section.failure_analysis.migration.execute.description")}</span>
          <Button
            variant="link"
            leftIcon={<PlayCircleLine />}
            onClick={handleMigration}
            disabled={isLoading}
          >
            {isLoading
              ? t("customization.section.failure_analysis.migration.execute.loading")
              : t("customization.section.failure_analysis.migration.execute.button")}
          </Button>
        </Text>
      </div>

      <Modal title="Migration Failures" isOpen={modalIsOpen} onRequestClose={closeModal}>
        <div className={failuresModalContent}>
          <Text type="h2" as="h2">
            {t("customization.section.failure_analysis.migration.execute.failures_title")}
          </Text>
          <div className={failuresList}>
            {failureDetails.map(failure => (
              <div key={failure.projectId} className={failureItem}>
                <Text type="body2" as="p">
                  <strong>
                    {t("customization.section.failure_analysis.migration.execute.project_id")}:
                  </strong>{" "}
                  {failure.projectId}
                </Text>
                <Text type="body2" as="p">
                  <strong>
                    {t("customization.section.failure_analysis.migration.execute.failure_reason")}:
                  </strong>{" "}
                  {failure.error}
                </Text>
              </div>
            ))}
          </div>
          <div>
            <Button onClick={closeModal} variant="primary">
              {t("customization.section.failure_analysis.migration.execute.close")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

const migrationContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.s};
  margin-bottom: ${spacings.m};
`;

const textContainer = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const failuresModalContent = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
  padding: ${spacings.m};
  max-height: 600px;
  overflow-y: auto;
`;

const failuresList = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
  padding: ${spacings.s};
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const failureItem = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
  padding: ${spacings.s};
  border-left: 4px solid #d32f2f;
  background-color: #fff;
  border-radius: 2px;
`;
